const GA_COOKIE_MAX_AGE_SECONDS = 2 * 365 * 24 * 60 * 60;
const GA_SESSION_COOKIE_MAX_AGE_SECONDS = 30 * 60;
const GA_MEASUREMENT_ID = "G-QM5VJMMHBZ";

export function generateGaClientId(now = Date.now()): string {
  return `${Math.floor(Math.random() * 2_147_483_647)}.${Math.floor(now / 1000)}`;
}

export function buildGaCookieValue(clientId: string): string {
  return `GA1.1.${clientId}`;
}

export function parseGaClientId(gaCookie: string): string | undefined {
  const parts = gaCookie.split(".");
  if (parts.length >= 4) {
    return parts.slice(2).join(".");
  }

  return undefined;
}

export function generateGaSessionId(now = Date.now()): string {
  return Math.floor(now / 1000).toString();
}

export function buildGaSessionCookieValue(
  sessionId: string,
  now = Date.now(),
): string {
  const timestamp = Math.floor(now / 1000);
  const hash = Math.floor(Math.random() * 2_147_483_647);
  return `GS2.1.s${sessionId}$o1$g0$t${timestamp}$j60$l0$h${hash}`;
}

export function parseGaSessionId(gaSessionCookie: string): string | undefined {
  if (gaSessionCookie.startsWith("GS2.")) {
    const stripped = gaSessionCookie.replace(/^GS2\.\d+\./, "");
    const parts = stripped.split("$");
    const sessionPart = parts.find((part) => part.startsWith("s"));
    return sessionPart?.slice(1);
  }

  const match = gaSessionCookie.match(/GS\d+\.\d+\.(\d+)\./);
  return match?.[1];
}

export function getGaSessionCookieName(
  measurementId = GA_MEASUREMENT_ID,
): string {
  return `_ga_${measurementId.replace(/^G-/, "").replace(/[^A-Za-z0-9]/g, "")}`;
}

/**
 * Read gtag's in-memory client_id via the gtag('get') API.
 * Returns undefined if gtag is not loaded or doesn't respond within 500ms.
 *
 * This is critical for session continuity: before consent, gtag fires
 * session_start with an ephemeral client_id that captures traffic source
 * (gclid, referrer). When consent is granted, we must persist THAT client_id
 * as the _ga cookie so subsequent events join the same GA4 session.
 */
export function getGtagClientId(
  measurementId = GA_MEASUREMENT_ID,
): Promise<string | undefined> {
  return new Promise((resolve) => {
    const gtag = (globalThis as Record<string, unknown>).gtag as
      | ((...args: unknown[]) => void)
      | undefined;

    if (typeof gtag !== "function") {
      resolve(undefined);
      return;
    }

    const timeout = setTimeout(() => resolve(undefined), 500);
    gtag("get", measurementId, "client_id", (clientId: string) => {
      clearTimeout(timeout);
      resolve(clientId || undefined);
    });
  });
}

export const gaCookieConfig = {
  maxAgeSeconds: GA_COOKIE_MAX_AGE_SECONDS,
  sessionMaxAgeSeconds: GA_SESSION_COOKIE_MAX_AGE_SECONDS,
  measurementId: GA_MEASUREMENT_ID,
} as const;
